import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/calendar-api";
import { CalendarApiError, calendarErrorCode } from "@/lib/calendar-api";
import { dayKey, type CalendarConnection, type StepSchedule } from "@/lib/calendar";
import { historyFrom, NO_HISTORY, type History } from "@/lib/calendar-slots";
import { useMomentum } from "@/hooks/use-momentum";

export const CALENDAR_CONNECTION_KEY = ["/api/calendar/connection"] as const;
export const CALENDAR_SCHEDULES_KEY = ["/api/calendar/schedules"] as const;
const BUSY_KEY = "/api/calendar/busy";

/**
 * The Google Calendar connection. `available` is false when the calendar API isn't
 * deployed yet (404), so entry points can keep showing "coming soon" instead of a
 * connect flow that can't finish.
 */
export function useCalendarConnection() {
  const query = useQuery({
    queryKey: CALENDAR_CONNECTION_KEY,
    queryFn: api.getConnection,
    staleTime: 30_000,
    retry: (count, err) => calendarErrorCode(err) !== "unavailable" && count < 1,
  });
  return {
    connection: query.data ?? null,
    isLoading: query.isLoading,
    available: calendarErrorCode(query.error) !== "unavailable",
  };
}

/** Free times are suggested only with Google's permission and the Settings switch on. */
export function canSuggestFreeTimes(connection: CalendarConnection | null): boolean {
  return !!connection && connection.status === "connected" && connection.access.freeBusy && connection.suggestFreeTimes;
}

/** Every step's place on the calendar, keyed by action id. Steps keep their dates after a disconnect. */
export function useStepSchedules(enabled = true) {
  const query = useQuery({
    queryKey: CALENDAR_SCHEDULES_KEY,
    queryFn: api.listSchedules,
    enabled,
    staleTime: 30_000,
    retry: (count, err) => calendarErrorCode(err) !== "unavailable" && count < 1,
  });
  const byAction = useMemo(() => new Map((query.data ?? []).map((s) => [s.actionId, s])), [query.data]);
  return { schedules: byAction, isLoading: query.isLoading };
}

/** Busy ranges between two dates (ranges only — Google never hands over names or details). */
export function useBusy(calendarId: string | undefined, from: Date, to: Date, enabled: boolean) {
  return useQuery({
    queryKey: [BUSY_KEY, calendarId, dayKey(from), dayKey(to)],
    queryFn: () => api.getBusy(calendarId!, from, to),
    enabled: enabled && !!calendarId,
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof CalendarApiError) && count < 1,
  });
}

/**
 * When this person tends to finish steps: the times their completed, scheduled steps
 * were on the calendar. Shapes the best fit ("Morning, like your last 3 completed steps").
 */
export function useCalendarHistory(): History {
  const { data: items } = useMomentum();
  const { schedules } = useStepSchedules();
  return useMemo(() => {
    if (!items) return NO_HISTORY;
    const now = new Date();
    const starts = items
      .filter((i) => i.completed)
      .flatMap((i) => schedules.get(i.id)?.events ?? [])
      .map((e) => new Date(e.start))
      .filter((d) => d < now);
    return historyFrom(starts);
  }, [items, schedules]);
}

function useScheduleWrite<V>(write: (vars: V) => Promise<StepSchedule | null>, actionIdOf: (vars: V) => string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (schedule, vars) => {
      const actionId = actionIdOf(vars);
      queryClient.setQueryData<StepSchedule[]>(CALENDAR_SCHEDULES_KEY, (list = []) => [
        ...list.filter((s) => s.actionId !== actionId),
        ...(schedule ? [schedule] : []),
      ]);
    },
    onError: (err) => {
      // Google ended the connection: re-read it so every surface shows "needs reconnecting".
      if (calendarErrorCode(err) === "connection_ended") queryClient.invalidateQueries({ queryKey: CALENDAR_CONNECTION_KEY });
    },
  });
}

export function useCreateSchedule() {
  return useScheduleWrite(api.createSchedule, (v) => v.actionId);
}

export function useMoveEvent() {
  return useScheduleWrite(
    (v: { actionId: string; eventId: string; start: string; end: string }) =>
      api.moveEvent(v.actionId, v.eventId, { start: v.start, end: v.end }),
    (v) => v.actionId,
  );
}

export function useReplaceRhythm() {
  return useScheduleWrite(
    (v: { actionId: string } & Parameters<typeof api.replaceRhythm>[1]) => api.replaceRhythm(v.actionId, v),
    (v) => v.actionId,
  );
}

export function useRemoveSchedule() {
  return useScheduleWrite(
    (v: { actionId: string; scope: "all" | "future" }) => api.removeSchedule(v.actionId, v.scope),
    (v) => v.actionId,
  );
}

export function useUpdateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.updateConnection,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: CALENDAR_CONNECTION_KEY });
      const previous = queryClient.getQueryData<CalendarConnection | null>(CALENDAR_CONNECTION_KEY);
      if (previous) queryClient.setQueryData(CALENDAR_CONNECTION_KEY, { ...previous, ...patch });
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(CALENDAR_CONNECTION_KEY, context.previous);
    },
    onSuccess: (connection) => {
      queryClient.setQueryData(CALENDAR_CONNECTION_KEY, connection);
      queryClient.invalidateQueries({ queryKey: [BUSY_KEY] });
    },
  });
}

export function useDisconnectCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.disconnect,
    onSuccess: () => {
      queryClient.setQueryData(CALENDAR_CONNECTION_KEY, null);
      queryClient.removeQueries({ queryKey: [BUSY_KEY] });
    },
  });
}

/** Ask the backend for Google's screen and go there, in this tab. */
export function useStartGoogleConnect() {
  return useMutation({
    mutationFn: ({ returnTo, freeBusy = true }: { returnTo: string; freeBusy?: boolean }) =>
      api.startGoogleConnect(returnTo, freeBusy),
    onSuccess: (url) => window.location.assign(url),
  });
}
